import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { apiClient } from "../../base_url/config"

// Basic API types
interface CartItemAdditional {
  id: number
  name: string
  price: number
  image?: string
}

interface CartMeal {
  id: number
  name: string
  price: number
  image?: string
  size?: string
  description?: string
  calories?: string
  product?: {
    id: number
    name: string
  }
  additionals?: CartItemAdditional[]
}

interface ApiCartItem {
  id: number
  item_id: number
  cart_id: number
  quantity: number
  unit_price: string
  total_unit_price: number
  meals: CartMeal[]
}

interface CartData {
  id: number
  total_price: number
  items: ApiCartItem[]
  // Add missing tax fields from API response
  tax_percentage?: string | number
  tax_value?: number
  sub_total?: number
  delivery_price?: number
  price?: number
}

interface CartItem {
  id: string
  name: string
  price: number
  quantity: number
  image: string
  size?: string
  selectedMeal?: number | { id: number }
  extras?: number[]
  meals?: any[]
  addons?: any[]
  totalUnitPrice?: number
}

interface PromoState {
  code: string | null
  status: 'idle' | 'submitting' | 'loading' | 'success' | 'applied' | 'error'
  discount: number
  message: string | null
  error: string | null
}

interface CartState {
  items: CartItem[]
  apiCartData: CartData | null
  subtotal: number
  total: number
  isLoading: boolean
  error: string | null
  lastSyncedAt: number | null
  updatingItemId: string | null
  removingItemId: string | null
  deletingCart: boolean
  promo: PromoState
}

const initialState: CartState = {
  items: [],
  apiCartData: null,
  subtotal: 0,
  total: 0,
  isLoading: false,
  error: null,
  lastSyncedAt: null,
  updatingItemId: null,
  removingItemId: null,
  deletingCart: false,
  promo: {
    code: null,
    status: 'idle',
    discount: 0,
    message: null,
    error: null
  }
}

// Fetch cart data from API
export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (token: string, { rejectWithValue }) => {
    try {
      
      // Validate token
      if (!token || token.length < 10) {
        throw new Error('Invalid token provided for cart fetch')
      }
      
      
      // Check if token is expired or invalid
      try {
        // Decode JWT token to check expiration
        const tokenPayload = JSON.parse(atob(token.split('.')[1]))
        const currentTime = Math.floor(Date.now() / 1000)
        
        if (tokenPayload.exp && tokenPayload.exp < currentTime) {
          throw new Error('Token expired')
        }
        
      } catch (tokenError) {
        throw new Error('Invalid token format')
      }
      
      const response = await apiClient.get('/qr/carts/items', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'platform': 'qr'
        },
        timeout: 10000,
      })
      return response.data.data
    } catch (error: any) {
      
      // Handle different error scenarios
      if (error.response?.status === 500 || error.response?.status === 404) {
        return { items: [], total_price: 0, id: null }
      }
      
      if (error.response?.status === 401) {
        // Try to get a fresh token
        const freshToken = localStorage.getItem('auth_token')
        if (freshToken && freshToken !== token) {
          // Don't make recursive call, just throw error to trigger re-login
          throw new Error('Token expired - please login again')
        }
        throw new Error('Authentication failed - please login again')
      }
      
      if (error.response?.status === 400) {
        throw new Error('Invalid request format')
      }
      
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch cart')
    }
  }
)

// Add item to cart via API - simplified version
export const addItemToCart = createAsyncThunk(
  'cart/addItemToCart',
  async ({ item, token }: { item: any; token: string }, { rejectWithValue }) => {
    try {
      
      // Validate item data
      if (!item || !item.id) {
        throw new Error('Invalid item data: missing id')
      }
      
      // Validate token
      if (!token || token.length < 10) {
        throw new Error('Invalid token provided')
      }
      
      // Get branch ID and type ID from URL params
      const urlParams = new URLSearchParams(window.location.search)
      const branchId = urlParams.get('branchId') ? parseInt(urlParams.get('branchId')!) : null
      const typeId = urlParams.get('typeId') ? parseInt(urlParams.get('typeId')!) : null
      
      // Validate required parameters
      if (!branchId || !typeId) {
        throw new Error('Missing required parameters: branchId and typeId must be provided in URL')
      }
      
      
      // Create the item structure - ensure proper data types
      const cartItem: any = {
        itemId: parseInt(item.id.toString()),
        quantity: parseInt((item.quantity || 1).toString()),
        meals: []
      }
      
      // Handle products with meals - ensure we always have at least one meal
      let mealId = null
      let selectedMealData = null
      
      if (item.selectedMeal) {
        mealId = parseInt(item.selectedMeal.toString())
        // Find the selected meal data
        if (item.meals) {
          selectedMealData = item.meals.find((meal: any) => meal.id === item.selectedMeal)
        }
      } else if (item.meals && item.meals.length > 0) {
        // Auto-select first meal if none selected
        const firstMeal = item.meals[0]
        mealId = parseInt(firstMeal.id.toString())
        selectedMealData = firstMeal
      }
      
      // For products with meals, use the meal structure
      if (mealId && selectedMealData) {
        cartItem.meals.push({
          mealId: mealId,
          additionals: (item.extras || []).map((extraId: any) => ({
            id: parseInt(extraId.toString()),
            quantity: 1
          }))
        })
        
        // Use the meal's item_id if available
        if (selectedMealData.item_id) {
          cartItem.itemId = parseInt(selectedMealData.item_id.toString())
        }
      } else {
        // For simple products without meals, create a default meal structure
        cartItem.meals.push({
          mealId: parseInt(item.id.toString()), // Use item.id as mealId
          additionals: (item.extras || []).map((extraId: any) => ({
            id: parseInt(extraId.toString()),
            quantity: 1
          }))
        })
      }
      
      
      // Create payload exactly like your working example
      const payload = {
        items: [cartItem],
        platform: "qr",
        typeId: typeId,
        branchId: branchId
      }
      
      // Validate payload before sending
      if (!payload.items || payload.items.length === 0) {
        throw new Error('Payload has no items')
      }
      
      payload.items.forEach((item: any, index: number) => {
        if (!item.itemId || item.itemId <= 0) {
          throw new Error(`Item ${index + 1} has invalid itemId: ${item.itemId}`)
        }
        if (!item.quantity || item.quantity <= 0) {
          throw new Error(`Item ${index + 1} has invalid quantity: ${item.quantity}`)
        }
        if (!item.meals || item.meals.length === 0) {
          throw new Error(`Item ${index + 1} has no meals`)
        }
        item.meals.forEach((meal: any, mealIndex: number) => {
          if (!meal.mealId || meal.mealId <= 0) {
            throw new Error(`Item ${index + 1}, Meal ${mealIndex + 1} has invalid mealId: ${meal.mealId}`)
          }
        })
      })
      
     
      const response = await apiClient.post('/qr/add-to-cart', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'platform': 'qr',
        },
        timeout: 15000,
      })
      
      return response.data.data
    } catch (error: any) {

      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to add item to cart')
    }
  }
)

// Update item quantity
export const updateItemQuantity = createAsyncThunk(
  'cart/updateItemQuantity',
  async ({ cartId, cartItemId, quantity, token }: { cartId: number; cartItemId: number; quantity: number; token: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `/qr/carts/${cartId}/item/${cartItemId}/quantity`,
        { quantity },
        {
          headers: {
            'Authorization': `Bearer ${token}`,

            'Accept': 'application/json',
          }
        }
      )
      return { cartItemId, quantity, data: response.data }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update quantity')
    }
  }
)

// Remove item from cart
export const removeCartItem = createAsyncThunk(
  'cart/removeCartItem',
  async ({ cartId, cartItemId, itemId, token }: { cartId: number; cartItemId: number; itemId: string; token: string }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/qr/carts/${cartId}/item/${cartItemId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,

          'Accept': 'application/json',
        }
      })
      return itemId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to remove item')
    }
  }
)

// Delete entire cart
export const deleteCart = createAsyncThunk(
  'cart/deleteCart',
  async ({ cartId, token }: { cartId: number; token: string }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/qr/carts/${cartId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,

          'Accept': 'application/json',
        }
      })
      return true
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete cart')
    }
  }
)

// Apply promo code
export const applyPromoCode = createAsyncThunk(
  'cart/applyPromoCode',
  async ({ code, subtotal, token }: { code: string; subtotal: number; token: string }, { rejectWithValue, dispatch, getState }) => {
    try {
      
      const response = await apiClient.post('/qr/cobones/check', {
        code,
        amount: subtotal // هذا الآن يتضمن subtotal + tablePrice
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      })
      
      const result = response.data
      
      // If successful, set a timer to change status from success to applied after 5 seconds
      if (result.success || (result.discount && result.discount > 0)) {
        setTimeout(() => {
          dispatch(setPromoApplied())
        }, 5000)
      }
      
      return result
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to apply promo code')
    }
  }
)

// Re-apply promo code when cart changes (for maintaining discount on quantity changes)
export const reapplyPromoCode = createAsyncThunk(
  'cart/reapplyPromoCode',
  async ({ token }: { token: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { cart: CartState }
      const { promo, subtotal } = state.cart
      
      // Only reapply if there's an active promo code
      if (!promo.code || promo.discount === 0) {
        return { success: false, message: 'No active promo code to reapply' }
      }
      
      const response = await apiClient.post('/qr/cobones/check', {
        code: promo.code,
        amount: subtotal
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      })
      
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to reapply promo code')
    }
  }
)

// Helper function for legacy sync (simplified)
export const syncCartWithAPI = createAsyncThunk(
  'cart/syncWithAPI',
  async (payload: { token: string; items?: any[] }, { rejectWithValue }) => {
    try {
      // For now, just return success - this can be implemented if needed
      return { success: true, message: 'Cart sync completed' }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sync cart with server')
    }
  }
)

  const cartSlice = createSlice({
    name: "cart",
    initialState,
    reducers: {
      addItem: (state, action: PayloadAction<CartItem>) => {
        const existingItem = state.items.find((item) => item.id === action.payload.id)
        if (existingItem) {
          existingItem.quantity += action.payload.quantity
        } else {
          state.items.push(action.payload)
        }
        state.total = state.items.reduce((sum: number, item: CartItem) => sum + item.price * item.quantity, 0)
      },
      removeItem: (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((item) => item.id !== action.payload)
        state.total = state.items.reduce((sum: number, item: CartItem) => sum + item.price * item.quantity, 0)
      },
      updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
        const item = state.items.find((item) => item.id === action.payload.id)
        if (item) {
          item.quantity = Math.max(0, action.payload.quantity)
          if (item.quantity === 0) {
            state.items = state.items.filter((i) => i.id !== action.payload.id)
          }
        }
        state.total = state.items.reduce((sum: number, item: CartItem) => sum + item.price * item.quantity, 0)
      },
      clearCart: (state) => {
        state.items = []
        state.total = 0
        state.error = null
        state.lastSyncedAt = null
      },
      clearError: (state) => {
        state.error = null
      },
      // Add action to update lastSyncedAt manually (when adding items via API)
      updateLastSyncedAt: (state) => {
        state.lastSyncedAt = Date.now()
      },
      // Add action to clean test/dummy items from cart
      cleanTestItems: (state) => {
        const originalLength = state.items.length
        state.items = state.items.filter(item => {
          const isTestItem = item.id === '15'
          return !isTestItem
        })

        if (state.items.length !== originalLength) {
          state.total = state.items.reduce((sum: number, item: CartItem) => sum + item.price * item.quantity, 0)
        }
      },
      // Promo code actions
      resetPromo: (state) => {
        state.promo = {
          code: null,
          status: 'idle',
          discount: 0,
          message: null,
          error: null
        }
        // Restore original total from API or subtotal
        if (state.apiCartData?.total_price != null) {
          state.total = toNumber(state.apiCartData.total_price)
        } else {
          state.total = state.subtotal
        }
      },
      setPromoApplied: (state) => {
        if (state.promo.status === 'success') {
          state.promo.status = 'applied'
        }
      },
      setPromoCode: (state, action: PayloadAction<string>) => {
        state.promo.code = action.payload
      },
    },
    extraReducers: (builder) => {
      builder
        // Fetch cart
        .addCase(fetchCart.pending, (state) => {
          state.isLoading = true
          state.error = null
        })
        .addCase(fetchCart.fulfilled, (state, action) => {
          state.isLoading = false
          state.apiCartData = action.payload
          state.error = null
          
          // Handle empty cart or no items
          if (!action.payload || !action.payload.items || action.payload.items.length === 0) {
            state.items = []
            state.subtotal = 0
            state.total = 0
            return
          }
          
          // Transform API data to normalized items
          const transformedItems = action.payload.items.map((item: ApiCartItem) => ({
            id: item.item_id.toString(),
            name: item.meals[0]?.name || 'Unknown Item',
            price: parseFloat(item.unit_price),
            quantity: item.quantity,
            image: item.meals[0]?.image || '/placeholder.svg',
            totalUnitPrice: item.total_unit_price,
            selectedMeal: item.meals[0]?.id,
            meals: item.meals,
            extras: item.meals[0]?.additionals?.map(addon => addon.id) || [],
            addons: item.meals[0]?.additionals || []
          }))
          state.items = transformedItems
          state.subtotal = transformedItems.reduce((sum: number, item: CartItem) => sum + (item.totalUnitPrice || item.price * item.quantity), 0)
          
          // استخدم total_price من API إذا كان متوفراً، وإلا احسب من subtotal
          if (action.payload.total_price != null) {
            state.total = toNumber(action.payload.total_price)
          } else {
            state.total = state.subtotal
          }
          
          // If there's an active promo code, we need to reapply it with new subtotal
          if (state.promo.code && state.promo.discount > 0) {
            // Temporarily set total without discount for now
            // The actual discount will be recalculated when reapplyPromoCode is called
            state.total = state.subtotal
          }
        })
        .addCase(fetchCart.rejected, (state, action) => {
          state.isLoading = false
          state.error = action.payload as string
        })
        
        // Add item to cart
        .addCase(addItemToCart.pending, (state) => {
          state.isLoading = true
          state.error = null
        })
        .addCase(addItemToCart.fulfilled, (state, action) => {
          state.isLoading = false
          state.apiCartData = action.payload
          // Transform API data to normalized items
          if (action.payload.items) {
            const transformedItems = action.payload.items.map((item: ApiCartItem) => ({
              id: item.item_id.toString(),
              name: item.meals[0]?.name || 'Unknown Item',
              price: parseFloat(item.unit_price),
              quantity: item.quantity,
              image: item.meals[0]?.image || '/placeholder.svg',
              totalUnitPrice: item.total_unit_price,
              selectedMeal: item.meals[0]?.id,
              meals: item.meals,
              extras: item.meals[0]?.additionals?.map(addon => addon.id) || [],
              addons: item.meals[0]?.additionals || []
            }))
            state.items = transformedItems
            state.subtotal = transformedItems.reduce((sum: number, item: CartItem) => sum + (item.totalUnitPrice || item.price * item.quantity), 0)
            
            // استخدم total_price من API إذا كان متوفراً، وإلا احسب من subtotal
            if (action.payload.total_price != null) {
              state.total = toNumber(action.payload.total_price)
            } else {
              state.total = state.subtotal
            }
            
            // If there's an active promo code, we need to reapply it with new subtotal
            if (state.promo.code && state.promo.discount > 0) {
              // Temporarily set total without discount for now
              // The actual discount will be recalculated when reapplyPromoCode is called
              state.total = state.subtotal
            }
          }
        })
        .addCase(addItemToCart.rejected, (state, action) => {
          state.isLoading = false
          state.error = action.payload as string
          
          // Fallback: Add to local cart
          if (action.meta.arg?.item) {
            const item = action.meta.arg.item
            state.items.push({
              id: item.id.toString(),
              name: item.name || 'Unknown Item',
              price: item.price || 0,
              quantity: item.quantity || 1,
              image: item.image || '/placeholder.svg',
              selectedMeal: item.selectedMeal,
              meals: item.meals || [],
              extras: item.extras || [],
              addons: item.addons || []
            })
            
            // Recalculate totals
            state.subtotal = state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
            state.total = state.subtotal - state.promo.discount
          }
        })
        
        // Update item quantity
        .addCase(updateItemQuantity.pending, (state, action) => {
          state.updatingItemId = action.meta.arg.cartItemId.toString()
        })
        .addCase(updateItemQuantity.fulfilled, (state, action) => {
          state.updatingItemId = null
          const { cartItemId, quantity, data } = action.payload
          
          
          // تحديث state.items - البحث باستخدام cartItemId (id من API)
          const item = state.items.find(i => i.id === cartItemId.toString())
          if (item) {
            item.quantity = quantity
            if (quantity === 0) {
              state.items = state.items.filter(i => i.id !== cartItemId.toString())
            }
          } else {
            
            // محاولة العثور على العنصر باستخدام item_id
            const itemByItemId = state.items.find(i => i.id === cartItemId.toString() || i.id === data?.item_id?.toString())
            if (itemByItemId) {
              itemByItemId.quantity = quantity
              if (quantity === 0) {
                state.items = state.items.filter(i => i.id !== itemByItemId.id)
              }
            } else {
              // إذا لم يتم العثور على العنصر، قم بتحديث state.items من apiCartData
              if (state.apiCartData && data) {
                // تحديث state.items من apiCartData
                state.items = state.apiCartData.items.map(apiItem => ({
                  id: apiItem.item_id.toString(),
                  name: apiItem.meals[0]?.name || 'Unknown Item',
                  price: parseFloat(apiItem.unit_price),
                  quantity: apiItem.quantity,
                  image: apiItem.meals[0]?.image || '/placeholder.svg',
                  totalUnitPrice: apiItem.total_unit_price,
                  selectedMeal: apiItem.meals[0]?.id,
                  meals: apiItem.meals,
                  extras: apiItem.meals[0]?.additionals?.map(addon => addon.id) || [],
                  addons: apiItem.meals[0]?.additionals || []
                }))
              }
            }
          }
          
          // تحديث apiCartData إذا كان متوفراً
          if (state.apiCartData && data) {
            const apiItem = state.apiCartData.items.find(i => i.id === cartItemId)
            if (apiItem) {
              apiItem.quantity = quantity
            } else {
            }
          }
          
          
          // Recalculate totals
          state.subtotal = state.items.reduce((sum: number, item: CartItem) => sum + (item.totalUnitPrice || item.price * item.quantity), 0)
          
          // If there's an active promo code, we need to reapply it with new subtotal
          if (state.promo.code && state.promo.discount > 0) {
            // Temporarily set total without discount for now
            // The actual discount will be recalculated when reapplyPromoCode is called
            state.total = state.subtotal
          } else {
            // No promo code, set total normally
            state.total = state.subtotal
          }
          
          
          // 🚀 إضافة: تحديث بيانات السلة من API بعد تعديل الكمية
          // هذا يضمن أن الأسعار والأسعار الفرعية تتزامن مع التغييرات
        })
        .addCase(updateItemQuantity.rejected, (state, action) => {
          state.updatingItemId = null
          state.error = action.payload as string
        })
        
        // Remove item
        .addCase(removeCartItem.pending, (state, action) => {
          state.removingItemId = action.meta.arg.itemId
        })
        .addCase(removeCartItem.fulfilled, (state, action) => {
          state.removingItemId = null
          state.items = state.items.filter(item => item.id !== action.payload)
          // Recalculate totals
          state.subtotal = state.items.reduce((sum: number, item: CartItem) => sum + (item.totalUnitPrice || item.price * item.quantity), 0)
          
          // If there's an active promo code, we need to reapply it with new subtotal
          if (state.promo.code && state.promo.discount > 0) {
            // Temporarily set total without discount for now
            // The actual discount will be recalculated when reapplyPromoCode is called
            state.total = state.subtotal
          } else {
            // No promo code, set total normally
            state.total = state.subtotal
          }
        })
        .addCase(removeCartItem.rejected, (state, action) => {
          state.removingItemId = null
          state.error = action.payload as string
        })
        
        // Delete cart
        .addCase(deleteCart.pending, (state) => {
          state.deletingCart = true
        })
        .addCase(deleteCart.fulfilled, (state) => {
          state.deletingCart = false
          state.items = []
          state.apiCartData = null
          state.subtotal = 0
          state.total = 0
          state.promo = {
            code: null,
            status: 'idle',
            discount: 0,
            message: null,
            error: null
          }
        })
        .addCase(deleteCart.rejected, (state, action) => {
          state.deletingCart = false
          state.error = action.payload as string
        })
        
        // Apply promo code
        .addCase(applyPromoCode.pending, (state) => {
          state.promo.status = 'loading'
          state.promo.error = null
        })
        .addCase(applyPromoCode.fulfilled, (state, action) => {
          const result = action.payload
          
          // تحقق من أن الكوبون تم تطبيقه بنجاح (إما success = true أو وجود discount)
          if (result.success || (result.discount && result.discount > 0)) {
            state.promo.status = 'success'
            // TODO: نحتاج طريقة لحفظ الكود الفعلي
            // مؤقتاً نستخدم 'PROMO_APPLIED' - نحتاج إرسال الكود الفعلي في payload
            state.promo.code = 'PROMO_APPLIED' // مؤقت - نحتاج الكود الفعلي
            state.promo.discount = result.discount || 0
            state.promo.message = result.message || 'Promo code applied successfully!'
            
            
            // استخدم final_amount من API response مباشرة
            if (result.final_amount != null) {
              state.total = result.final_amount
            } else {
              // احسب المجموع الجديد بعد الخصم
              // نستخدم subtotal فقط (بدون tablePrice) لأن tablePrice سيتم إضافته لاحقاً
              const originalAmount = state.subtotal || 0
              
              state.total = originalAmount - state.promo.discount
            }
            
            // تأكد من أن state.total تم تحديثه
          } else {
            state.promo.status = 'error'
            state.promo.error = result.message || 'Invalid promo code'
          }
        })
        .addCase(applyPromoCode.rejected, (state, action) => {
          state.promo.status = 'error'
          state.promo.error = action.payload as string
        })
        
        // Reapply promo code
        .addCase(reapplyPromoCode.pending, (state) => {
          // Keep existing promo status but mark as updating
          state.promo.status = 'loading'
        })
        .addCase(reapplyPromoCode.fulfilled, (state, action) => {
          const result = action.payload
          
          if (result.success) {
            // Update discount amount while keeping the same promo code
            state.promo.discount = result.discount || 0
            state.promo.status = 'applied'
            
            // Update total with new discount
            if (result.final_amount != null) {
              state.total = result.final_amount
            } else {
              const originalAmount = result.amount || state.subtotal || 0
              state.total = originalAmount - state.promo.discount
            }
          } else {
            // If reapplication fails, reset promo
            state.promo = {
              code: null,
              status: 'idle',
              discount: 0,
              message: null,
              error: null
            }
            // Restore original total
            if (state.apiCartData?.total_price != null) {
              state.total = toNumber(state.apiCartData.total_price)
            } else {
              state.total = state.subtotal
            }
          }
        })
        .addCase(reapplyPromoCode.rejected, (state, action) => {
          // If reapplication fails, reset promo
          state.promo = {
            code: null,
            status: 'idle',
            discount: 0,
            message: null,
            error: null
          }
          // Restore original total
          if (state.apiCartData?.total_price != null) {
            state.total = toNumber(state.apiCartData.total_price)
          } else {
            state.total = state.subtotal
          }
        })
        
        // Original sync cart
        .addCase(syncCartWithAPI.pending, (state) => {
          state.isLoading = true
          state.error = null
        })
        .addCase(syncCartWithAPI.fulfilled, (state, action) => {
          state.isLoading = false
          state.error = null
          state.lastSyncedAt = Date.now()
        })
        .addCase(syncCartWithAPI.rejected, (state, action) => {
          state.isLoading = false
          state.error = action.payload as string
        })
    },
})

export const { addItem, removeItem, updateQuantity, clearCart, clearError, cleanTestItems, updateLastSyncedAt, resetPromo, setPromoApplied } = cartSlice.actions
export default cartSlice.reducer

// Utility functions for number normalization
const toNumber = (v: unknown): number => (v == null || v === '' ? 0 : Number(v))
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

// Selectors for totals
export const selectSubtotal = (state: { cart: CartState }) => {
  const apiData = state.cart.apiCartData
  
  // استخدم sub_total إذا كان متوفراً، وإلا استخدم total_price
  if (apiData?.sub_total != null) {
    const result = toNumber(apiData.sub_total)
    return result
  }
  if (apiData?.total_price != null) {
    const result = toNumber(apiData.total_price)
    return result
  }
  const result = state.cart.subtotal
  return result
}

export const selectTax = (state: { cart: CartState }) => {
  const apiData = state.cart.apiCartData
  if (apiData?.tax_value != null && apiData.tax_value > 0) {
    return toNumber(apiData.tax_value)
  }
  if (apiData?.sub_total != null && apiData?.tax_percentage != null) {
    return round2(toNumber(apiData.sub_total) * (toNumber(apiData.tax_percentage) / 100))
  }
  // إذا لم تكن هناك ضريبة من API، احسبها من subtotal
  const subtotal = selectSubtotal(state)
  // افترض نسبة ضريبة 15% إذا لم تكن محددة
  return round2(subtotal * 0.15)
}

export const selectDelivery = (state: { cart: CartState }) => 
  toNumber(state.cart.apiCartData?.delivery_price) ?? 0

export const selectTotal = (state: { cart: CartState }) => {
  const discount = state.cart.promo.discount
  const hasActivePromo = state.cart.promo.code && discount > 0
  const subtotal = selectSubtotal(state)
  
  
  // If there's an active promo code, calculate the discounted total
  if (hasActivePromo && discount > 0) {
    // Use the discount from promo state to calculate final total
    // لا نضيف الضريبة لأنها محسوبة في الباك إند
    const discountedTotal = subtotal - discount
    const result = round2(Math.max(0, discountedTotal))
    return result
  }
  
  // If no promo code, use API total or calculate from subtotal
  const apiData = state.cart.apiCartData
  if (apiData?.total_price != null) {
    const result = toNumber(apiData.total_price)
    return result
  }
  
  // Calculate from subtotal only (without tax - tax is calculated in backend)
  const result = round2(subtotal)
  return result
}

export const selectPromoDiscount = (state: { cart: CartState }) => state.cart.promo.discount
